import React, { useContext } from 'react';
import './AddToCart.css';
import CartIcon from '../../assets/CartIcon.jsx';
import BinIcon from '../../assets/BinIcon.jsx';
import PlusIcon from '../../assets/PlusIcon.jsx';
import { getLastIndexById } from '../../services/Find.js';
import PropTypeShapes from '../../services/Constants/PropTypeShapes.jsx';
import { CartContext, UpdateCartContext } from '../../services/context/CartContext.js';
import { defaultAnalyticsVariables, events, pagePrefix, hotDeals } from '../../services/Constants/Analytics.js';
import { AnalyticsPageContext } from '../../services/context/AnalyticsPageContext.js';
import { getVendorCategory, getVoucherVendor } from '../../services/ReverseHierarchySearch.js';
import { CategoriesContext } from '../../services/context/CategoriesContext.js';
import { Voucher } from '../../services/Constants/ObjectDefinitions.js';
import MinusIcon from '../../assets/MinusIcon.jsx';
import { UpdateSnackBarContext } from '../../services/context/SnackBarContext.js';
import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';
import FlagIcon from '../../assets/FlagIcon.jsx';
import { cartMax, homePageCartEvent, hotDealsPageCartEvent, hotDealsSliderCartEvent, invalidDealsCartEvent } from '../../services/Constants/config.js';
import useNormalizedSubscriptionStatus from '../../services/utils/subscriptionUtils.js';
import PropTypes from 'prop-types';

/**
 *
 * @param {object} props - the props of the component
 * @param {Voucher} props.voucher - the voucher associated with the component
 * @returns {JSX.Element} AddToCartButtons Component
 * @param {string} props.pageLabel - optional fromPage string variable
 */
export default function AddToCartButtons({ voucher, pageLabel }) {
  const cart = useContext(CartContext);
  const setCart = useContext(UpdateCartContext);
  const analyticsPage = useContext(AnalyticsPageContext);
  const categories = useContext(CategoriesContext);
  const MoneyFormatter = useContext(MoneyFormatterContext);
  const updateSnackBar = useContext(UpdateSnackBarContext);
  const subscriptionStatus = useNormalizedSubscriptionStatus();
  const productPackageName = `Pay ${MoneyFormatter.format(MoneyFormatter.getValue(voucher.cost))}, get ${MoneyFormatter.format(MoneyFormatter.getValue(voucher.options.buyNow.value))}`;

  const cartAnalyticsData = {
    ...defaultAnalyticsVariables,
    page_name: analyticsPage,
    product_category: getVendorCategory(categories, getVoucherVendor(categories, voucher))?.name,
    product_name: voucher.name,
    product_price: MoneyFormatter.getValue(voucher.cost).amount,
    product_id: voucher.id,
    product_quantity: 1,
  };

  const hotDealsAnalytics = {
    ...cartAnalyticsData,
    product_variant: hotDeals,
    product_package_name: productPackageName,
    visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
  };

  const decrease = () => {
    switch (pageLabel) {
      case homePageCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: landing`,
          event_name: [
            events.interaction,
            events.removeFromCart,
          ],
          link_id: `${pagePrefix}: hot deals for you: cart remove`,
        });
        break;
      }

      case hotDealsPageCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: hot deals`,
          event_name: [
            events.interaction,
            events.removeFromCart,
          ],
          link_id: `${pagePrefix}: hot deals: cart remove`,
        });
        break;
      }

      case hotDealsSliderCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: hot deals`,
          event_name: [
            events.interaction,
            events.removeFromCart,
          ],
          link_id: `${pagePrefix}: hot deals: view deal: cart remove`,
        });
        break;
      }

      case invalidDealsCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: hot deals: offer expired`,
          event_name: [
            events.interaction,
            events.removeFromCart,
          ],
          link_id: `${pagePrefix}: hot deals: expired: cart remove`,
        });
        break;
      }

      default: {
        window.utag?.link({
          ...cartAnalyticsData,
          event_name: [
            events.interaction,
            events.removeFromCart,
          ],
          link_id: `${pagePrefix}: cart remove`,
        });
        break;
      }
    }

    const lastIndex = getLastIndexById(cart, voucher);
    setCart([
      ...cart.slice(0, lastIndex),
      ...cart.slice(lastIndex + 1),
    ]);
  };

  const increase = () => {
    if (cart.length >= cartMax) {
      updateSnackBar({
        icon: <FlagIcon />,
        body: 'Cart limit reached',
      });
      return;
    }

    switch (pageLabel) {
      case homePageCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: landing`,
          event_name: [
            events.interaction,
            events.addToCart,
          ],
          link_id: `${pagePrefix}: hot deals for you: add to cart`,
        });
        break;
      }

      case hotDealsPageCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: hot deals`,
          event_name: [
            events.interaction,
            events.addToCart,
          ],
          link_id: `${pagePrefix}: hot deals: add to cart`,
        });
        break;
      }

      case hotDealsSliderCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: hot deals`,
          event_name: [
            events.interaction,
            events.addToCart,
          ],
          link_id: `${pagePrefix}: hot deals: view deal: add to cart`,
        });
        break;
      }

      case invalidDealsCartEvent: {
        window.utag?.link({
          ...hotDealsAnalytics,
          page_name: `${pagePrefix}: hot deals: offer expired`,
          event_name: [
            events.interaction,
            events.addToCart,
          ],
          link_id: `${pagePrefix}: hot deals: expired: add to cart`,
        });
        break;
      }

      default: {
        window.utag?.link({
          ...cartAnalyticsData,
          event_name: [
            events.interaction,
            events.addToCart,
          ],
          link_id: `${pagePrefix}: cart add`,
        });
        break;
      }
    }

    setCart([
      ...cart,
      voucher,
    ]);
  };

  const num = cart.filter(item => item.id === voucher.id).length;

  return (
    <div className='add-to-cart container-vh'>
      {
        num === 0
        && <button
          onClick={ increase }
          className='icon'
        >
          <CartIcon />
        </button>

      }
      {
        num > 0
        && <>
          <button
            onClick={ decrease }
            className='icon'
          >
            {num > 1 ? <MinusIcon /> : <BinIcon />}
          </button>
          <p className='amount'>{num}</p>
          <button
            onClick={ increase }
            className='icon'
          >
            <PlusIcon />
          </button>

        </>
      }
    </div>
  );
}

AddToCartButtons.propTypes = {
  voucher: PropTypeShapes.voucher,
  pageLabel: PropTypes.string,
};
